import { type Action, createHook, createStore } from 'react-sweet-state';

import {
	type AppConfigStoreState as AppConfigStoreState,
	type EmbedConfig,
	type TeamsClientConfig,
	type TeamsNavigationConfig,
	type TeamsPreviewPanelConfig,
	type TeamsProductConfig,
} from '../types';

const defaultNavigationState: TeamsNavigationConfig = {
	contextSpaPaths: [],
	contextEntryPoint: null,
};

const defaultClientConfigState: TeamsClientConfig = {
	cloudId: null,
	stargateRoot: '',
	env: undefined,
};

const defaultTeamsProductConfigState: TeamsProductConfig = {
	appName: null,
	productName: null,
	isJiraAvailable: false,
	openLinksInNewTab: false,
};

const defaultTeamsPreviewPanelConfigState: TeamsPreviewPanelConfig = {
	openPreviewPanel: () => {},
};

const defaultEmbedConfigState: EmbedConfig = {
	openGiveKudosInNewTab: false,
};

const defaultState: AppConfigStoreState = {
	...defaultNavigationState,
	...defaultClientConfigState,
	...defaultTeamsProductConfigState,
	...defaultTeamsPreviewPanelConfigState,
	...defaultEmbedConfigState,
	docTitle: {},
};

const actions = {
	setNavigationConfig:
		(navigationConfig: TeamsNavigationConfig): Action<AppConfigStoreState> =>
		({ setState }) => {
			setState({
				...navigationConfig,
			});
		},
	setClientConfig:
		(clientConfig: TeamsClientConfig): Action<AppConfigStoreState> =>
		({ setState }) => {
			setState({
				...clientConfig,
			});
		},
	setTeamsProductConfig:
		(teamsProductConfig: TeamsProductConfig): Action<AppConfigStoreState> =>
		({ setState }) => {
			setState({
				...teamsProductConfig,
			});
		},
	setTeamsPreviewPanelConfig:
		(teamsPreviewPanelConfig: TeamsPreviewPanelConfig): Action<AppConfigStoreState> =>
		({ setState }) => {
			setState({
				...teamsPreviewPanelConfig,
			});
		},
	setEmbedConfig:
		(embedConfig: EmbedConfig): Action<AppConfigStoreState> =>
		({ setState }) => {
			setState({
				...embedConfig,
			});
		},
	// Set all config at once
	setAppConfig:
		(appConfig: AppConfigStoreState): Action<AppConfigStoreState> =>
		({ setState }) => {
			setState({
				...appConfig,
			});
		},
};

type AppConfigActions = typeof actions;

const AppConfigStore = createStore<AppConfigStoreState, AppConfigActions>({
	initialState: defaultState,
	actions,
});

export const useAppConfigStore = createHook(AppConfigStore);
const useAppConfigActions = createHook(AppConfigStore, { selector: null });

export const useAppConfigStoreState = () => useAppConfigStore()[0];

export const useSetTeamsConfigActions = () => {
	return useAppConfigActions()[1];
};

export const useAppName = () => useAppConfigStoreState().appName;

export const useNavigationState = () => {
	const { contextSpaPaths, contextEntryPoint } = useAppConfigStoreState();
	return { contextSpaPaths, contextEntryPoint };
};
